<script>
  import { createEventDispatcher } from "svelte";
  import { Link } from "framework7-svelte";

  export let orders = [];
  export let filteredOrders = [];
  export let pagedOrders = [];
  export let orderCounts = { all: 0, open: 0, filled: 0, rejected: 0 };
  export let openOrdersInFiltered = [];
  export let orderSearchQuery = "";
  export let orderStatusFilter = "ALL";
  export let orderSortCol = "time";
  export let orderSortDir = "desc";
  export let orderPageSize = 100;
  export let pendingActionIds = new Set();
  export let flashingRowIds = new Set();
  export let engineConnected = false;
  export let terminalEnv = "LIVE";
  export let isSplit = false;

  const dispatch = createEventDispatcher();

  function toggleSort(col) {
    dispatch("sort", col);
  }

  function handleCancel(ord) {
    dispatch("cancelOrder", ord);
  }

  function handleBulkCancel() {
    dispatch("bulkCancel");
  }

  function handleClientClick(client, user) {
    dispatch("openClient", { client, user });
  }

  function isOrderActive(ord) {
    if (!ord) return false;
    const s = (ord.status || "").toUpperCase();
    return (
      ["PENDING", "OPEN", "TRIGGER_PENDING", "ACK", "CANCELLING..."].includes(s) ||
      pendingActionIds.has(ord.id)
    );
  }
</script>

<div class="card data-table terminal-grid-card">
  <div class="card-header display-flex justify-content-space-between align-items-center">
    <div style="display:flex; align-items:center; gap:8px;">
      <span style="font-weight:700;">
        Live Order Book
      </span>
      <span class="grid-badge-total">{filteredOrders.length} order{filteredOrders.length === 1 ? "" : "s"}</span>
    </div>
    {#if !isSplit}
      <span style="font-size: 12px; color: #64748b;">Click UCC Tag to inspect • Click headers to sort</span>
    {/if}
  </div>

  <!-- Quick Filter & Bulk Actions Toolbar -->
  <div class="grid-toolbar">
    <div class="toolbar-left">
      <!-- Text Search -->
      <div class="search-input-wrapper">
        <span class="search-icon">🔍</span>
        <input
          type="text"
          class="grid-search-input"
          placeholder={isSplit ? "Search orders..." : "Search Symbol, UCC, Name, Order ID..."}
          bind:value={orderSearchQuery}
        />
        {#if orderSearchQuery}
          <button class="clear-search-btn" on:click={() => (orderSearchQuery = "")}>✕</button>
        {/if}
      </div>

      <!-- Segmented Status Pills -->
      <div class="filter-pill-group">
        <button
          class="filter-pill {orderStatusFilter === 'ALL' ? 'active' : ''}"
          on:click={() => (orderStatusFilter = "ALL")}
        >
          ALL <span class="pill-count">({orderCounts.all})</span>
        </button>
        <button
          class="filter-pill {orderStatusFilter === 'OPEN' ? 'active' : ''}"
          on:click={() => (orderStatusFilter = "OPEN")}
        >
          OPEN <span class="pill-count">({orderCounts.open})</span>
        </button>
        <button
          class="filter-pill {orderStatusFilter === 'FILLED' ? 'active' : ''}"
          on:click={() => (orderStatusFilter = "FILLED")}
        >
          FILLED <span class="pill-count">({orderCounts.filled})</span>
        </button>
        <button
          class="filter-pill {orderStatusFilter === 'REJECTED' ? 'active' : ''}"
          on:click={() => (orderStatusFilter = "REJECTED")}
        >
          REJ/CXL <span class="pill-count">({orderCounts.rejected})</span>
        </button>
      </div>
    </div>

    <div class="toolbar-right">
      <!-- Page Size -->
      {#if !isSplit}
        <div class="page-size-select-wrap">
          <span class="toolbar-label">Show:</span>
          <select bind:value={orderPageSize} class="grid-select">
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="ALL">All</option>
          </select>
        </div>
      {/if}

      <!-- Bulk Cancel Filtered Button -->
      <button
        class="bulk-action-btn bulk-btn-cancel"
        disabled={openOrdersInFiltered.length === 0}
        on:click={handleBulkCancel}
        title="Cancel all open orders matching current filter"
      >
        🛑 Cancel ({openOrdersInFiltered.length})
      </button>
    </div>
  </div>

  <div class="card-content" style="padding:0; overflow-x: auto; max-height: calc(100vh - 280px); overflow-y: auto;">
    <table class="terminal-compact-table">
      <thead>
        <tr>
          <th class="label-cell sortable-th" on:click={() => toggleSort("id")}>
            ID <span class="sort-icon">{orderSortCol === "id" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("client")}>
            Client UCC <span class="sort-icon">{orderSortCol === "client" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("symbol")}>
            Symbol <span class="sort-icon">{orderSortCol === "symbol" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("side")}>
            Side <span class="sort-icon">{orderSortCol === "side" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("qty")}>
            Qty <span class="sort-icon">{orderSortCol === "qty" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="numeric-cell sortable-th" on:click={() => toggleSort("price")}>
            Price <span class="sort-icon">{orderSortCol === "price" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("status")}>
            Status <span class="sort-icon">{orderSortCol === "status" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell sortable-th" on:click={() => toggleSort("time")}>
            Time <span class="sort-icon">{orderSortCol === "time" ? (orderSortDir === "asc" ? "▲" : "▼") : "↕"}</span>
          </th>
          <th class="label-cell" style="min-width: 110px;">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#if pagedOrders.length === 0}
          <tr>
            <td colspan="9" style="text-align:center; padding: 24px; color: #94a3b8;">
              {orders.length === 0
                ? (engineConnected ? "No orders in engine order book" : "Engine disconnected / No live orders")
                : "No orders match the current filter criteria"}
            </td>
          </tr>
        {:else}
          {#each pagedOrders as ord (ord.id)}
            <tr
              class="{pendingActionIds.has(ord.id) ? 'row-pending-lease' : ''} {flashingRowIds.has(ord.id) ? 'flash-update-up' : ''}"
            >
              <td class="label-cell font-mono-tabular" style="font-size:11px;" title={ord.id}>
                {ord.id.length > 14 ? ord.id.slice(0, 6) + '...' + ord.id.slice(-4) : ord.id}
              </td>
              <td class="label-cell">
                <button
                  type="button"
                  class="client-pill-btn"
                  on:click={() => handleClientClick(ord.client, ord.user)}
                  title="Click to inspect client controls"
                >
                  <span class="client-tag-badge">{ord.client}</span>
                  {#if ord.user}
                    <span class="user-subname">{ord.user.fullName}</span>
                  {:else}
                    <span class="user-subname unlinked">Unlinked</span>
                  {/if}
                </button>
              </td>
              <td class="label-cell font-bold">{ord.symbol}</td>
              <td class="label-cell" style="color: {ord.side === 'BUY' ? '#16a34a' : '#dc2626'}; font-weight:bold;">
                {ord.side}
              </td>
              <td class="numeric-cell font-mono-tabular">{ord.qty}</td>
              <td class="numeric-cell font-mono-tabular">₹{Number(ord.price || 0).toFixed(2)}</td>
              <td class="label-cell">
                <span class="badge {ord.status === 'FILLED' ? 'color-green' : (ord.status === 'ACK' || ord.status === 'OPEN') ? 'color-blue' : ord.status === 'CANCELLING...' ? 'color-pink' : 'color-orange'}">
                  {ord.status}
                </span>
              </td>
              <td class="label-cell font-mono-tabular" style="font-size:11px;">{ord.time}</td>
              <td class="label-cell">
                <div style="display:flex; align-items:center; gap:4px;">
                  {#if isOrderActive(ord) || pendingActionIds.has(ord.id)}
                    <button
                      type="button"
                      class="row-action-btn btn-cancel"
                      disabled={pendingActionIds.has(ord.id)}
                      on:click={() => handleCancel(ord)}
                      title="Cancel this order immediately"
                    >
                      {pendingActionIds.has(ord.id) ? "⏳ Cancelling" : "❌ Cancel"}
                    </button>
                  {/if}
                  <Link
                    href={`/user-trading-controls/?ucc=${encodeURIComponent(ord.client)}`}
                    class="button button-small button-outline button-round"
                    style="display: inline-flex; align-items: center; gap: 2px; padding: 0 6px; font-size:10px; height:20px; line-height:20px;"
                  >
                    &rarr;
                  </Link>
                </div>
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</div>
